import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { PostfixPopupLink } from 'shared/components/PostfixPopupLink';
import { useFilterChangeCallback, useRuntimeSetting } from 'shared/hooks';
import { selectCommercialLink } from 'shared/selectors/commercialLink';
import { selectDealType, selectJsonQuery, selectOfferType } from 'shared/selectors/jsonQuery';
import { selectRegionId } from 'shared/selectors/location';

import { FDealType, FOfferType, TJsonQuery, terms } from '../../../../../packages/JsonQuery';
import { FILTER_KEY } from '../../constants';

const COMMERCIAL_ENABLED_KEY = 'isNewCommercialSerpDesign.Desktop.Enabled';

interface IOfferTypePopupPostfixProps {
  onClose?: () => void;
}

function getNextOfferType(isCurrentCommercial: boolean, dealType: FDealType): FOfferType {
  if (isCurrentCommercial) {
    return (dealType & FDealType.Sale) !== 0 ? FOfferType.FlatNew : FOfferType.FlatOld;
  }

  return FOfferType.Office;
}

/**
 * Смена вертикали сбрасывает все фильтры: у жилой и коммерции разные наборы термов, а термы без
 * availability (has_video, with_layout, floor, publish_period…) иначе переезжают в чужой запрос
 * и опустошают выдачу (CD-291907). Переживают только тип сделки (он живёт в `_type`), регион
 * и версия движка.
 */
export function createVerticalSwitchJsonQuery(jsonQuery: TJsonQuery, regionId: number): TJsonQuery {
  return {
    _type: jsonQuery._type,
    engine_version: jsonQuery.engine_version,
    region: jsonQuery.region ?? terms([regionId]),
  };
}

export function OfferTypePopupPostfix({ onClose }: IOfferTypePopupPostfixProps) {
  const dealType = useSelector(selectDealType);
  const offerType = useSelector(selectOfferType);
  const commercialLink = useSelector(selectCommercialLink);
  const jsonQuery = useSelector(selectJsonQuery);
  const regionId = useSelector(selectRegionId);
  const isCommercialEnabled = useRuntimeSetting<boolean>(COMMERCIAL_ENABLED_KEY, false);

  const onChange = useFilterChangeCallback(FILTER_KEY);

  const isCurrentCommercial = (offerType & FOfferType.Commercial) !== 0;

  const handleInlineToggle = useCallback(() => {
    const nextOfferType = getNextOfferType(isCurrentCommercial, dealType);

    onClose?.();
    onChange(
      { action: 'setJsonQuery', arguments: [createVerticalSwitchJsonQuery(jsonQuery, regionId)] },
      { action: 'setOfferType', arguments: [nextOfferType] },
    );
  }, [isCurrentCommercial, dealType, jsonQuery, regionId, onClose, onChange]);

  if (isCommercialEnabled) {
    if (dealType === FDealType.RentDaily) {
      return null;
    }

    const text = isCurrentCommercial ? 'Жилая' : 'Коммерческая';
    const href = commercialLink ?? '/';

    return <PostfixPopupLink href={href} text={text} onClick={handleInlineToggle} />;
  }

  if (dealType === FDealType.RentDaily || !commercialLink) {
    return null;
  }

  return <PostfixPopupLink href={commercialLink} text="Коммерческая" />;
}
